import { Button, CButtonClassname, EButtonColor, Gauge, SectionTitle } from '@web_opxp/components';
import { ProgressSelectors, StatsSelectors, renderStringToReact, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { GAMEPLAY_FULLSCREEN, SKELETON_CLASSNAME, SKELETON_DUMMYTEXT } from '../../base/constants';
import * as FullscreenHelper from '../../base/fullscreenHelper';
import HintPopup from '../../components/HintPopup/HintPopup';
import { CyclesSelectors } from '../../cycles';
import { FeedbackDialog, FeedbackSelectors } from '../../feedback';
import { SessionSelectors } from '../../session';
import { IState } from '../../store/state';
import DeltaBadge from '../components/DeltaBadge';
import * as Selectors from '../selectors';
import { ExerciseActions } from '../slice';
import './EvaluationView.scss';
import ExerciseProgress from './ExerciseProgress';
import WorkoutSurvey from './WorkoutSurvey';

export interface IEvaluationViewProps {
  loading: boolean;
  nextWorkoutReady: boolean;
  goal: number;
  score: number;
  comparison: number;
  delta: number;
  workoutId: number;
  nextIndex: number;
  nextExerciseLink: string;
  completionCount: number;
  fullscreen: boolean;
  isFirstSession: boolean;
  onFirstSessionComplete: () => void;
  onSessionComplete: () => void;
  onRepeatExercise: () => void;
  onPostWorkoutFeedback: (result: any) => void;
  showFeedbackDialog: boolean;
}

export const EvaluationViewFC: React.FC<IEvaluationViewProps> = ({
  loading,
  nextWorkoutReady,
  goal,
  score,
  comparison,
  delta,
  workoutId,
  nextIndex,
  nextExerciseLink,
  completionCount,
  fullscreen,
  isFirstSession,
  onFirstSessionComplete,
  onSessionComplete,
  onRepeatExercise,
  onPostWorkoutFeedback,
  showFeedbackDialog,
}) => {
  const { t } = useTranslation();

  const skeletonClass = loading ? SKELETON_CLASSNAME : '';
  const fullscreenClass = fullscreen ? FullscreenHelper.FULLSCREEN_CLASS : '';
  const nextButtonSkeleton = nextWorkoutReady ? '' : SKELETON_CLASSNAME;

  let title: string;

  if (loading) {
    title = SKELETON_DUMMYTEXT;
  } else if (score >= goal) {
    title = t('Exercise.PostWorkout.GoalAchieved');
  } else {
    title = t('Exercise.PostWorkout.DefaultTitle');
  }
  const titleClasses = `exercise-title ${skeletonClass}`;
  const cardClasses = `evaluation-card ${skeletonClass}`;
  const gaugeClasses = `comparison-speedometer ${skeletonClass}`;
  const gaugeId = `evaluation-${workoutId}`;

  const [surveyOpen, setSurvey] = React.useState(completionCount <= 1);
  const toggleSurvey = () => setSurvey(!surveyOpen);
  const onCompleteSurvey = (result: any) => {
    onPostWorkoutFeedback(result);
    toggleSurvey();
  };
  const percent = Math.floor(comparison);
  // See BW-3645 for issue
  // consider comparison as loading when its = 0 and loading flag is set
  // if loading flag is set but comparison is > 0 - its not reloading
  const gaugeLoading = loading && comparison === 0;

  const buttonLabel = t('Exercise.PostWorkout.ScoreHintDialogOKLabel');
  const hintPopupTitle = t('Exercise.PostWorkout.ScoreHintDialogTitle');
  const hintPopupContent = t('Exercise.PostWorkout.ScoreHintContentHtml');

  const contentReactElement = renderStringToReact(hintPopupContent);

  const handleCompleteClick = () => {
    if (isFirstSession) {
      onFirstSessionComplete();
      return;
    }

    onSessionComplete();
  };

  return (
    <div className="view evaluation-view">
      <SectionTitle className={titleClasses}>{title}</SectionTitle>
      <div className="evaluation-cards-conatiner">
        <div className={`${cardClasses} score-card`}>
          <h2>
            {t('Exercise.PostWorkout.Score')}{' '}
            <span className="h2 score-goal-ratio">
              {score}
              <span className="h2 score-goal"> / {goal}</span>
            </span>
            <HintPopup title={hintPopupTitle} iconSize={24} okButtonLabel={buttonLabel}>
              {contentReactElement}
            </HintPopup>
          </h2>
          <div className={gaugeClasses}>
            <h3 className="nn-button-medium">{percent}%</h3>
            {!gaugeLoading && (
              <Gauge
                id={gaugeId}
                amount={percent}
                minLabel={t('Exercise.Skill.Min')}
                maxLabel={t('Exercise.Skill.Max')}
              />
            )}
          </div>
          <DeltaBadge delta={delta} />
        </div>
        <div className={`${cardClasses} progress-card`}>
          {surveyOpen && !showFeedbackDialog ? (
            <WorkoutSurvey onCompleteSurvey={onCompleteSurvey} />
          ) : (
            <ExerciseProgress />
          )}
        </div>
      </div>
      <div className="evaluation-actions">
        {nextExerciseLink ? (
          <Link
            to={{
              pathname: `${nextExerciseLink}`,
              state: { index: nextIndex },
            }}
            className={`${CButtonClassname} ${EButtonColor.blue} ${nextButtonSkeleton}`}>
            {t('Exercise.PostWorkout.Actions.Continue')}
          </Link>
        ) : (
          <Button
            className={nextButtonSkeleton}
            label={t('Exercise.PostWorkout.Actions.Continue')}
            color={EButtonColor.blue}
            onClick={handleCompleteClick}
          />
        )}
        <Button
          className={`${skeletonClass} ${fullscreenClass}`}
          label={t('Exercise.PostWorkout.Actions.Repeat')}
          color={EButtonColor.white}
          onClick={onRepeatExercise}
        />
        <Button
          className={skeletonClass}
          disabled={surveyOpen}
          label={t('Exercise.PostWorkout.Actions.Survey')}
          color={EButtonColor.white}
          onClick={toggleSurvey}
        />
      </div>
      <FeedbackDialog />
    </div>
  );
};

export const mapStateToProps = (state: IState) => {
  const goal = Selectors.getLastWorkoutGoal(state);
  const cycleId = CyclesSelectors.getCurrentCycleId(state);
  const sessionId = CyclesSelectors.getCurrentSessionId(state);
  const workoutId = Selectors.getWorkoutId(state);
  const currentScore = ProgressSelectors.getLastWorkoutScore(state, workoutId);
  const comparison = SessionSelectors.getWorkoutComparison(state, workoutId);
  // get delta not only for current workout but also for workouts of the same group
  const lastScore = Selectors.getLastWorkoutScore(state);
  const delta = currentScore - lastScore;
  // in evaluation view we assume that current workout is finished and active workout is the next to start
  const nextIndex = SessionSelectors.getActiveWorkoutRelativeIndex(state);
  const nextWorkoutId = SessionSelectors.getActiveWorkoutId(state);
  const nextExerciseLink = SessionSelectors.getWorkoutExerciseIdForLink(state, nextWorkoutId);
  const completionCount = ProgressSelectors.getWorkoutCompletionCount(state, workoutId);
  const fullscreen = !!StatsSelectors.getStatBooleanValueById(state, GAMEPLAY_FULLSCREEN);
  const sessionLoading = SessionSelectors.isLoading(state);
  const statsSaving = StatsSelectors.isSaving(state);
  const exerciseLoading = Selectors.isLoading(state);
  const nextWorkoutReady = !sessionLoading && !statsSaving && !exerciseLoading;
  const showFeedbackDialog = FeedbackSelectors.getFeedbackShow(state);
  const isFirstSession = cycleId === 1 && sessionId === 1;
  return {
    loading: sessionLoading,
    goal,
    score: currentScore,
    comparison: comparison ?? 0,
    delta,
    workoutId,
    nextIndex,
    nextExerciseLink,
    completionCount,
    fullscreen,
    nextWorkoutReady,
    showFeedbackDialog,
    isFirstSession,
  };
};

export const mapDispatchToProps = {
  onFirstSessionComplete: ExerciseActions.completeFirstSession,
  onSessionComplete: ExerciseActions.completeSession,
  onRepeatExercise: ExerciseActions.repeatExercise,
  onPostWorkoutFeedback: ExerciseActions.postWorkoutFeedback,
};

const EvaluationView = connect(mapStateToProps, mapDispatchToProps)(EvaluationViewFC);
EvaluationView.displayName = 'EvaluationView';

export default EvaluationView;
